import { Fragment } from 'react'
import Image from 'next/image'
import Container from '@/components/structure/Container'
import RichText from '@/components/content/common/RichText'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import type { Block } from '@/types/app'
import type { Media, Page } from '@/payload-types'

type QuoteTemplate = Extract<NonNullable<Page['layout']>[number], { blockType: 'quoteTemplate' }>
type QuoteBlockProps = Pick<QuoteTemplate, 'quote' | 'authors'> & { editor?: boolean }

export default function QuoteBlock({ quote, authors, editor }: QuoteBlockProps) {
  const content = (
    <div className={cn('line line-y relative w-full', !editor && 'lg:p-1/12')}>
      <div
        className={cn(
          'relative flex h-full w-full flex-col gap-6 border-dark-100 bg-white p-base',
          !editor && 'lg:border',
        )}
      >
        <Icon name="lightbulb" className="absolute left-base top-base md:left-12 md:top-10" />
        <h1 className={cn('px-0 pt-8 md:px-16 md:pt-5 [&>div>p]:!font-bold', editor && 'title')}>
          <RichText content={quote.root.children as Block[]} />
        </h1>
        <Authors authors={authors} />
      </div>
    </div>
  )

  if (editor) {
    return <div className="my-base">{content}</div>
  }

  return <Container>{content}</Container>
}

export function Authors({ authors }: Pick<QuoteBlockProps, 'authors'>) {
  return (
    <div className="flex shrink-0 flex-col gap-2.5 text-13 font-semibold sm:flex-row sm:items-center md:ml-auto md:px-5">
      <div className="flex items-center">
        {authors?.map((person, index) => {
          const profileImage = person.image as Media

          if (!profileImage) return null

          return (
            <Image
              key={person.id}
              alt={person.name as string}
              src={profileImage.url as string}
              width={40}
              height={40}
              className="relative -ml-1.5 h-10 w-10 rounded-full object-cover outline outline-2 outline-ui-background first:ml-0 last:border-0"
              style={{ zIndex: authors.length - index }}
            />
          )
        })}
      </div>
      <div className="flex items-center gap-1">
        {authors && (
          <p className="line-clamp-1 capitalize">
            {authors.map((person, index) => (
              <Fragment key={person.id}>
                {person.name}
                {index < authors.length - 2 && ', '}
                {index === authors.length - 2 && <span className="mx-1 text-primary-500">&</span>}
              </Fragment>
            ))}
          </p>
        )}
      </div>
    </div>
  )
}
